import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Briefcase, Check } from "lucide-react";
import { adminGetJobs, adminCreateJob, adminUpdateJob, adminDeleteJob, adminGetCategories } from "../../api";

const emptyJob = (catIndex = 0) => ({
  category_index: catIndex,
  salary: "",
  title: { pl: "", en: "", uk: "", es: "" },
  location: { pl: "", en: "", uk: "", es: "" },
  type: { pl: "", en: "", uk: "", es: "" },
  active: true,
});

const LangFields = ({ label, value, onChange }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-white/75">{label}</label>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {["pl", "en", "uk", "es"].map((l) => (
        <input
          key={l}
          value={value[l] || ""}
          onChange={(e) => onChange(l, e.target.value)}
          placeholder={l.toUpperCase()}
          className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/45"
        />
      ))}
    </div>
  </div>
);

const JobEditor = ({ initial, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyJob(categories[0]?.index ?? 0));
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const setLoc = (field) => (lang, val) =>
    setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: val } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      category_index: Number(form.category_index),
      salary: form.salary,
      title: form.title,
      location: form.location,
      type: form.type,
      active: form.active,
    };
    try {
      const saved = isEdit
        ? await adminUpdateJob(initial.id, payload)
        : await adminCreateJob(payload);
      onSaved(saved, isEdit);
    } catch (e) {
      alert("Nie udało się zapisać ogłoszenia.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 py-10">
      <div className="glass-card w-full max-w-2xl rounded-3xl p-7 sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">
            {isEdit ? "Edytuj ogłoszenie" : "Nowe ogłoszenie"}
          </h3>
          <button onClick={onClose} className="glass-btn p-2">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-white/75">Kategoria</label>
              <select
                value={form.category_index}
                onChange={(e) => setForm((f) => ({ ...f, category_index: Number(e.target.value) }))}
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-white/45 [&>option]:bg-zinc-900"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.index}>{c.name?.pl}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-white/75">Wynagrodzenie</label>
              <input
                value={form.salary}
                onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))}
                placeholder="np. 4 800 – 6 200 PLN"
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/45"
              />
            </div>
          </div>

          <LangFields label="Stanowisko (PL / EN / UA / ES)" value={form.title} onChange={setLoc("title")} />
          <LangFields label="Lokalizacja (PL / EN / UA / ES)" value={form.location} onChange={setLoc("location")} />
          <LangFields label="Typ zatrudnienia (PL / EN / UA / ES)" value={form.type} onChange={setLoc("type")} />

          <button
            onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
            className="flex items-center gap-3 text-sm text-white/80"
          >
            <span className={`flex h-6 w-11 items-center rounded-full px-0.5 transition-colors ${form.active ? "bg-white" : "bg-white/20"}`}>
              <span className={`h-5 w-5 rounded-full bg-black transition-transform ${form.active ? "translate-x-5" : ""}`} />
            </span>
            {form.active ? "Aktywne (widoczne na stronie)" : "Nieaktywne (ukryte)"}
          </button>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="glass-btn px-5 py-2.5 text-sm font-medium">
            Anuluj
          </button>
          <button
            onClick={save}
            disabled={saving || !form.title.pl}
            className="glass-btn-solid glass-btn px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Zapisywanie...</> : <><Check className="h-4 w-4" /> Zapisz</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const JobsPanel = () => {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // job object or {} for new
  const [open, setOpen] = useState(false);

  const catName = (index) => {
    const c = categories.find((cat) => cat.index === index);
    return c ? c.name?.pl : "";
  };

  const load = () => {
    setLoading(true);
    Promise.all([adminGetJobs(), adminGetCategories()])
      .then(([jobsData, catsData]) => {
        setJobs(Array.isArray(jobsData) ? jobsData : []);
        setCategories(Array.isArray(catsData) ? catsData : []);
      })
      .catch(() => {
        setJobs([]);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = (saved, isEdit) => {
    setJobs((js) => (isEdit ? js.map((j) => (j.id === saved.id ? saved : j)) : [saved, ...js]));
    setOpen(false);
    setEditing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć to ogłoszenie?")) return;
    await adminDeleteJob(id);
    setJobs((js) => js.filter((j) => j.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-white/50">
        <Loader2 className="h-5 w-5 animate-spin" /> Ładowanie ogłoszeń...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Ogłoszenia</h2>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="glass-btn-solid glass-btn px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Dodaj ogłoszenie
        </button>
      </div>

      {jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-20 text-white/50">
          <Briefcase className="h-8 w-8" />
          <p className="text-sm">Brak ogłoszeń. Dodaj pierwsze!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="glass-card flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wider text-white/60">
                    {catName(job.category_index)}
                  </span>
                  {!job.active && (
                    <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wider text-white/50">
                      Ukryte
                    </span>
                  )}
                </div>
                <h4 className="mt-1.5 font-display text-lg font-semibold text-white">{job.title?.pl}</h4>
                <p className="text-xs text-white/55">
                  {job.location?.pl} · {job.type?.pl} · {job.salary}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditing(job); setOpen(true); }}
                  className="glass-btn px-4 py-2 text-xs font-medium"
                >
                  <Pencil className="h-4 w-4" /> Edytuj
                </button>
                <button
                  onClick={() => remove(job.id)}
                  className="glass-btn px-4 py-2 text-xs font-medium text-red-300 hover:text-red-200"
                >
                  <Trash2 className="h-4 w-4" /> Usuń
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <JobEditor
          initial={editing}
          categories={categories}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default JobsPanel;
